import classNames from 'classnames'
import {Icon} from 'quickstart/components/content/Icon'
import {Stack} from 'quickstart/components/layout/Stack'
import {wrapChildren} from 'quickstart/utils'
import {
  Children,
  cloneElement,
  ComponentProps,
  isValidElement,
  ReactElement,
} from 'react'
import {partitionProps} from 'tizra'
import S from './styles.module.scss'

export interface ButtonProps extends ComponentProps<'button'> {
  fluid?: boolean
  hoverable?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg'
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'link'
    | 'stealth'
    | 'nav'
    | 'unstyled'
  icon?: ComponentProps<typeof Icon>['icon'] | ReactElement
  rightIcon?: ComponentProps<typeof Icon>['icon'] | ReactElement
  as?: any
}

const _Button = ({
  children,
  fluid = false,
  hoverable = true,
  icon,
  rightIcon,
  size = 'lg',
  variant = 'primary',
  as = 'button',
  ...props
}: ButtonProps) => {
  const AsComponent = as as 'button'
  if (typeof icon === 'string') icon = <Icon icon={icon} />
  if (typeof rightIcon === 'string') rightIcon = <Icon icon={rightIcon} />
  return (
    <AsComponent
      {...props}
      className={classNames(
        S[variant],
        S[size],
        fluid && S.fluid,
        hoverable && S.hoverable,
        props.className,
      )}
    >
      {icon}
      {wrapChildren(children)}
      {rightIcon}
    </AsComponent>
  )
}

export interface ButtonGroupProps extends ComponentProps<typeof Stack> {
  disabled?: boolean
  hoverable?: boolean
  size?: ButtonProps['size']
  variant?: ButtonProps['variant']
}

const Group = ({children, ...props}: ButtonGroupProps) => {
  const [groupProps, rest] = partitionProps(
    ['disabled', 'hoverable', 'size', 'variant'],
    props,
  )

  const childArray = Children.toArray(children)
  const primaryIndex = childArray.findIndex(x => isValidElement(x))

  return (
    <Stack row spacing="1rem" {...rest}>
      {childArray.map((child, i) =>
        isValidElement<object>(child) ?
          cloneElement(child, {
            variant: i === primaryIndex ? 'primary' : 'secondary',
            ...groupProps,
            ...child.props,
          })
        : child,
      )}
    </Stack>
  )
}

export const Button = Object.assign(_Button, {Group})
